import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { Tweet as TweetType } from '@/types/tweet';
import type { ConversationThreadResult } from './useConversationThread';

interface ConversationThreadProperties {
  className?: string | undefined;
  highlightTweetId?: string | undefined;
  renderTweet: (tweet: TweetType) => ReactNode;
  thread: ConversationThreadResult;
}

export function ConversationThread({ thread, renderTweet, highlightTweetId, className }: ConversationThreadProperties) {
  return (
    <div className={cn('space-y-3', className)}>
      {thread.tweetIds.map((id, index) => {
        const tweet = thread.tweets[id];
        if (!tweet) {
          return null;
        }

        const meta = tweet._conversationMeta;
        const isHighlighted = tweet.id === highlightTweetId;

        return (
          <div
            key={id}
            className={cn(
              'group relative transition-colors hover:bg-muted/40',
              meta.depth > 0 && 'ml-4 sm:ml-8 max-w-[calc(100%-1rem)] sm:max-w-[calc(100%-2rem)]',
              meta.isOriginalPoster && 'bg-primary/10',
              isHighlighted && 'ring-2 ring-primary/60',
              index === 0 && 'border-t border-border/60'
            )}
          >
            {meta.depth > 0 && <div className="absolute left-0 top-0 bottom-0 w-0.5 -ml-2 sm:-ml-4 bg-muted" />}

            {meta.isOriginalPoster && (
              <div className="absolute top-2 right-2 z-layer-surface">
                <span className="rounded border border-info/40 bg-info/10 px-1.5 py-0.5 text-xs text-info-foreground">
                  OP
                </span>
              </div>
            )}

            <div className="w-full max-w-full overflow-hidden">{renderTweet(tweet)}</div>
          </div>
        );
      })}
    </div>
  );
}
